import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Icon } from '~/eds/src/components/Icon/Icon';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { TruncateText } from '~/eds/src/components/Text/subcomponents/TruncateText';
import { useRouting } from '~/routing/useRouting';

import { getNodeIconName } from '../helpers/getResourceIcon';
import { getResourceUrl } from '../helpers/getResourceUrl';
import { EnrichedGroupNode } from '../helpers/NodeEnricher/NodeEnricher';
import { TreeBrowserNode } from '../helpers/TreeBrowser/types';

export const DEFAULT_INDENT_PX = 20;
const DRAG_DATA_TYPE = 'application/x-workflow-node-id';
const HOLD_OVER_DELAY_MS = 500;

// Module-level variable to track currently dragging node
// (getData() returns empty during dragover due to browser security)
let currentlyDraggingNodeId: string | null = null;

export interface DropInfo {
  nodeId: string;
  newParentNodeId: string;
}

const messages = defineMessages({
  toggleCollapse: {
    id: 'WorkflowGroupNodesTable.toggleCollapse',
    defaultMessage: 'Toggle collapse',
    description: 'Button label for toggling collapse/expand',
  },
  accessDenied: {
    id: 'WorkflowGroupNodesTable.accessDenied',
    defaultMessage: 'Restricted File',
    description:
      'Text shown when user does not have permission to view the item name',
  },
});

export interface NameCellProps<T = TreeBrowserNode<EnrichedGroupNode>> {
  indentAmount?: number;
  indentLevel?: number;
  isExpanded?: boolean;
  isLoading?: boolean;
  name?: string;
  node: T;
  isPermissionDenied?: boolean;
  isValidDropTarget?: (draggedNodeId: string, targetNodeId: string) => boolean;
  onCollapse?: (node: T) => void;
  onExpand?: (node: T) => void;
  onDrop?: (info: DropInfo) => void;
  onHeldOver?: (node: T) => void;
}

export const NameCell = ({
  name,
  indentAmount = DEFAULT_INDENT_PX,
  indentLevel = 0,
  isExpanded,
  isLoading,
  isPermissionDenied,
  node,
  isValidDropTarget,
  onCollapse,
  onExpand,

  onDrop,
  onHeldOver,
}: NameCellProps) => {
  const intl = useIntl();
  const hasChildren = node.attributes.isParent;
  const indent = indentAmount * (indentLevel - 1);
  const toggle = isExpanded ? onCollapse : onExpand;
  const { navigate } = useRouting();
  const url = getResourceUrl(node);
  const [isDragOver, setIsDragOver] = useState(false);
  const holdOverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoldOverTimer = useCallback(() => {
    if (holdOverTimerRef.current) {
      clearTimeout(holdOverTimerRef.current);
      holdOverTimerRef.current = null;
    }
  }, []);

  // Cleanup timer on unmount
  useEffect(() => clearHoldOverTimer, [clearHoldOverTimer]);

  const hasAccess = !isPermissionDenied && name;

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    currentlyDraggingNodeId = node.id;
    e.dataTransfer.setData(DRAG_DATA_TYPE, node.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    currentlyDraggingNodeId = null;
  };

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!onDrop) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      if (
        !currentlyDraggingNodeId ||
        isValidDropTarget?.(currentlyDraggingNodeId, node.id) === false
      ) {
        return;
      }

      setIsDragOver(true);

      // Start hold-over timer if not already running
      if (!holdOverTimerRef.current && onHeldOver) {
        holdOverTimerRef.current = setTimeout(() => {
          holdOverTimerRef.current = null;
          onHeldOver(node);
        }, HOLD_OVER_DELAY_MS);
      }
    },
    [onDrop, node, onHeldOver, isValidDropTarget],
  );

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
    clearHoldOverTimer();
  }, [clearHoldOverTimer]);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      clearHoldOverTimer();

      if (!onDrop) return;

      const droppedNodeId = e.dataTransfer.getData(DRAG_DATA_TYPE);
      if (isValidDropTarget && isValidDropTarget(droppedNodeId, node.id)) {
        onDrop({ nodeId: droppedNodeId, newParentNodeId: node.id });
      }
    },
    [onDrop, node.id, clearHoldOverTimer, isValidDropTarget],
  );

  const formattedName = useMemo(() => {
    if (!hasAccess) {
      return intl.formatMessage(messages.accessDenied);
    }

    return name;
  }, [hasAccess, name, intl]);

  return (
    <Layout
      pl={indent}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      align="center"
      styles={{
        flex: '1 1 0',
        minWidth: 0,
        minHeight: 40,
        cursor: 'grab',
        borderRadius: '4px',
        transition: 'background-color 0.15s ease',
        ...(isDragOver && {
          backgroundColor: 'status.active.light',
        }),
      }}
    >
      {hasChildren && (
        <IconButton
          text={intl.formatMessage(messages.toggleCollapse)}
          isLoading={isLoading}
          icon={isExpanded ? 'caret-down' : 'caret-right'}
          onClick={() => toggle?.(node)}
          disabled={isLoading}
        />
      )}
      <Layout
        align="center"
        spacing={1}
        pl={hasChildren ? 0 : 40}
        styles={{
          flex: '1 1 0',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Icon
          icon={!hasChildren && isLoading ? 'loading' : getNodeIconName(node)}
          size="m"
          flex="none"
        />
        <TruncateText
          onClick={() => url && hasAccess && navigate(url)}
          color={hasAccess ? 'action.link' : 'text.secondary'}
          style={{ cursor: hasAccess ? 'pointer' : 'default' }}
        >
          {formattedName}
        </TruncateText>
      </Layout>
    </Layout>
  );
};
